import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import LogoutIcon from '@mui/icons-material/Logout'
import SchoolIcon from '@mui/icons-material/School'
import ReplayIcon from '@mui/icons-material/Replay'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { BackButton } from 'components/UnvUI'
import { Oval } from 'react-loader-spinner'
import Swal from 'sweetalert2'

import {
  getStudent,
  academicLeave,
  expelStudent,
  graduateStudent,
  restoreStudent,
} from 'service/StudentLifecycleService'

const ACTIONS = {
  academic_leave: {
    titleKey: 'auto.studentlifecycle.action_academic_leave',
    titleFallback: 'Академический отпуск',
    fn: academicLeave,
    fields: ['reason', 'start_date', 'end_date', 'order_number'],
    dateFields: ['start_date', 'end_date'],
  },
  expel: {
    titleKey: 'auto.studentlifecycle.action_expel',
    titleFallback: 'Отчисление',
    fn: expelStudent,
    fields: ['reason', 'order_number', 'order_date'],
    dateFields: ['order_date'],
  },
  graduate: {
    titleKey: 'auto.studentlifecycle.action_graduate',
    titleFallback: 'Выпуск',
    fn: graduateStudent,
    fields: ['order_number', 'order_date', 'diploma_number'],
    dateFields: ['order_date'],
  },
  restore: {
    titleKey: 'auto.studentlifecycle.action_restore',
    titleFallback: 'Восстановление',
    fn: restoreStudent,
    fields: ['reason', 'order_number', 'order_date'],
    dateFields: ['order_date'],
  },
}

const StudentLifecyclePage = () => {
  const { t } = useTranslation('common')
  const { id } = useParams()
  const navigate = useNavigate()
  const [student, setStudent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionKey, setActionKey] = useState(null)
  const [formValues, setFormValues] = useState({})
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await getStudent(id)
      setStudent(data)
    } catch (e) {
      setError(e?.response?.data?.detail || t('auto.studentlifecycle.load_error', 'Не удалось загрузить данные студента'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const openDialog = (key) => {
    setActionKey(key)
    setFormValues({})
  }

  const handleSubmit = async () => {
    const action = ACTIONS[actionKey]
    if (!action) return
    setBusy(true)
    try {
      const res = await action.fn(id, formValues)
      const blob = res.data
      if (blob && blob instanceof Blob) {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${actionKey}_${id}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
      Swal.fire({ icon: 'success', title: t('auto.studentlifecycle.done', 'Готово'), text: t('auto.studentlifecycle.order_created', 'Приказ сформирован'), timer: 1800, showConfirmButton: false })
      setActionKey(null)
      await load()
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('auto.studentlifecycle.error', 'Ошибка'), text: e?.response?.data?.detail || t('auto.studentlifecycle.op_failed', 'Не удалось выполнить операцию') })
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}>
        <Oval height={48} width={48} color="#FF7A00" secondaryColor="#ffe0c2" />
      </Box>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <BackButton style={{ marginTop: 16 }} />
      </Container>
    )
  }

  const action = actionKey ? ACTIONS[actionKey] : null

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <BackButton variant="ghost" />
        <Typography variant="h5" fontWeight={700}>
          {student?.full_name || t('auto.studentlifecycle.student_n', 'Студент #{{id}}', { id })}
        </Typography>
      </Stack>

      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">{t('auto.studentlifecycle.group', 'Группа')}</Typography>
            <Typography>{student?.group_name || student?.group?.name || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">{t('auto.studentlifecycle.course', 'Курс')}</Typography>
            <Typography>{student?.course || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">{t('auto.studentlifecycle.status', 'Статус')}</Typography>
            <Typography>{student?.status || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">{t('auto.studentlifecycle.education_form', 'Форма обучения')}</Typography>
            <Typography>{student?.education_form || '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button fullWidth variant="outlined" color="warning" startIcon={<EventBusyIcon />} onClick={() => openDialog('academic_leave')}>
          {t('auto.studentlifecycle.academic_leave_short', 'Академ.отпуск')}
        </Button>
        <Button fullWidth variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={() => openDialog('expel')}>
          {t('auto.studentlifecycle.expel', 'Отчислить')}
        </Button>
        <Button fullWidth variant="outlined" color="success" startIcon={<SchoolIcon />} onClick={() => openDialog('graduate')}>
          {t('auto.studentlifecycle.graduate', 'Выпустить')}
        </Button>
        <Button fullWidth variant="outlined" color="info" startIcon={<ReplayIcon />} onClick={() => openDialog('restore')}>
          {t('auto.studentlifecycle.restore', 'Восстановить')}
        </Button>
      </Stack>

      <Dialog open={!!actionKey} onClose={() => !busy && setActionKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{action ? t(action.titleKey, action.titleFallback) : ''}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {action?.fields.map((f) => (
              <Grid item xs={12} key={f}>
                <TextField
                  fullWidth
                  type={action.dateFields.includes(f) ? 'date' : 'text'}
                  label={f}
                  InputLabelProps={action.dateFields.includes(f) ? { shrink: true } : undefined}
                  multiline={f === 'reason'}
                  minRows={f === 'reason' ? 3 : 1}
                  value={formValues[f] || ''}
                  onChange={(e) => setFormValues({ ...formValues, [f]: e.target.value })}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionKey(null)} disabled={busy}>{t('auto.studentlifecycle.cancel', 'Отмена')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={busy}
            startIcon={busy ? <Oval height={14} width={14} color="#fff" secondaryColor="#ffe0c2" /> : null}
            sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#e96d00' } }}
          >
            {t('auto.studentlifecycle.confirm_and_download', 'Подтвердить и скачать приказ')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default StudentLifecyclePage
